import { createUserFileUpload, fetchApi } from './UserApi';
import type { CouncilRegion, FoodLicense, PublicLiabilityInsurance } from 'shared-components/src/Types/DBTypes';

export type RegionRequired = {
	CouncilRegionId: number;
	MarketName: string;
};

export type NextBooking = {
	MarketDayDate: number;
};

export type ClientDocumentSummary = {
	ClientDocumentId: number;
	ClientDocumentOriginalFilename: string;
	ClientDocumentMimeType: string;
	ClientDocumentExtension: string;
	ClientDocumentSize: number;
	Created: number;
};

export type FoodLicenseWithDocuments = FoodLicense & {
	Documents?: ClientDocumentSummary[];
};

export type PublicLiabilityInsuranceWithDocuments = PublicLiabilityInsurance & {
	Documents?: ClientDocumentSummary[];
};

type LoadDataResponse = {
	food_licenses: FoodLicenseWithDocuments[] | false;
	public_liability: PublicLiabilityInsuranceWithDocuments[] | false;
	council_regions: CouncilRegion[] | false;
	regions_required: RegionRequired[] | false;
	next_bookings: NextBooking[] | false;
};

export type InsuranceLicenseData = {
	foodLicenses: FoodLicenseWithDocuments[];
	publicLiability: PublicLiabilityInsuranceWithDocuments[];
	councilRegions: CouncilRegion[];
	regionsRequired: RegionRequired[];
	nextBookings: NextBooking[];
};

export type AddLiabilityInsurancePayload = {
	MarketClientInsuranceName: string;
	MarketClientInsurancePolicyNumber: string;
	MarketClientInsuranceExpiry: string;
	MarketClientInsuranceValidFrom: string;
	files: File[];
};

export type AddFoodLicensePayload = {
	CouncilRegionId: number;
	FoodLicenseNumber: string;
	FoodLicenseType: 'Temporary' | 'Mobile';
	FoodLicenseExpiry: string;
	files: File[];
};

const normalizeArray = <T>(value: T[] | false | null | undefined): T[] => {
	if (!Array.isArray(value)) {
		return [];
	}
	return value;
};

const normalizeLoadData = (data: LoadDataResponse): InsuranceLicenseData => {
	return {
		foodLicenses: normalizeArray(data.food_licenses),
		publicLiability: normalizeArray(data.public_liability),
		councilRegions: normalizeArray(data.council_regions),
		regionsRequired: normalizeArray(data.regions_required),
		nextBookings: normalizeArray(data.next_bookings),
	};
};

export default class InsuranceLicenseService {
	public static async loadData(hash: string) {
		const response = await fetchApi<LoadDataResponse>({
			route: 'insurance-license/api/load-data',
			data: { hash },
			errorMessage: 'Error loading insurance and licenses',
		});
		return normalizeLoadData(response.data);
	}

	public static async addLiabilityInsurance(hash: string, payload: AddLiabilityInsurancePayload) {
		const upload = createUserFileUpload(hash);
		const formData = new FormData();
		formData.append('MarketClientInsuranceName', payload.MarketClientInsuranceName);
		formData.append('MarketClientInsurancePolicyNumber', payload.MarketClientInsurancePolicyNumber);
		formData.append('MarketClientInsuranceExpiry', payload.MarketClientInsuranceExpiry);
		formData.append('MarketClientInsuranceValidFrom', payload.MarketClientInsuranceValidFrom);
		payload.files.forEach((file) => {
			formData.append('files[]', file);
		});

		const response = await upload<LoadDataResponse>({
			route: 'insurance-license/api/add-liability-insurance',
			data: formData,
			errorMessage: 'Error adding liability insurance',
		});
		return normalizeLoadData(response.data);
	}

	public static async removeLiabilityInsurance(hash: string, policyId: number) {
		const response = await fetchApi<LoadDataResponse>({
			route: 'insurance-license/api/remove-liability-insurance',
			data: {
				hash,
				policy_id: policyId,
			},
			errorMessage: 'Error removing liability insurance',
		});
		return normalizeLoadData(response.data);
	}

	public static async addFoodLicense(hash: string, payload: AddFoodLicensePayload) {
		const upload = createUserFileUpload(hash);
		const formData = new FormData();
		formData.append('CouncilRegionId', String(payload.CouncilRegionId));
		formData.append('FoodLicenseNumber', payload.FoodLicenseNumber);
		formData.append('FoodLicenseType', payload.FoodLicenseType);
		formData.append('FoodLicenseExpiry', payload.FoodLicenseExpiry);
		payload.files.forEach((file) => {
			formData.append('files[]', file);
		});

		const response = await upload<LoadDataResponse>({
			route: 'insurance-license/api/add-food-license',
			data: formData,
			errorMessage: 'Error adding food license',
		});
		return normalizeLoadData(response.data);
	}

	public static async removeFoodLicense(hash: string, licenseId: number) {
		const response = await fetchApi<LoadDataResponse>({
			route: 'insurance-license/api/remove-food-license',
			data: {
				hash,
				license_id: licenseId,
			},
			errorMessage: 'Error removing food license',
		});
		return normalizeLoadData(response.data);
	}
}
